import React from "react";
import { useTranslation } from "react-i18next";

import { GanttIcon } from "./ganttIcon";
import { KanbanIcon } from "./kanbanIcon";
import { SynthesisIcon } from "./synthesisIcon";
import { TimeSheetIcon } from "./timeSheetIcon";
import { DiscussionsIcon } from "./discussionsIcon";
import { DocumentsIcon } from "./documentsIcon";
import { BudgetIcon } from "./budgetIcon";
import { CalendarIcon } from "./calendarIcon";
import { ListIcon } from "./listIcon";
import { ReportsIcon } from "./reportsIcon";
import { ToggleInput } from "./toggleInput";
import { Props } from "./types";
import styles from "./styles.scss";
import classNames from "classnames";
import { projectConfiguration } from "@planzone/dto";

function ProjectToolsConfiguration({ configuration, setConfiguration }: Props) {
  const { t } = useTranslation();
  const parameters = [
    {
      icon: SynthesisIcon,
      key: "isSynthesisEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.synthesis.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.synthesis.description",
      ),
      isEnabled: configuration.isSynthesisEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          isSynthesisEnabled: !configuration.isSynthesisEnabled,
        });
      },
    },
    {
      icon: ListIcon,
      key: "isListsListEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.listsList.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.listsList.description",
      ),
      isEnabled: configuration.isListsListEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          isListsListEnabled: !configuration.isListsListEnabled,
        });
      },
    },
    {
      icon: GanttIcon,
      key: "isGanttEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.gantt.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.gantt.description",
      ),
      isEnabled: configuration.isGanttEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          isGanttEnabled: !configuration.isGanttEnabled,
        });
      },
    },
    {
      icon: CalendarIcon,
      key: "isCalendarEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.calendar.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.calendar.description",
      ),
      isEnabled: configuration.isCalendarEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          isCalendarEnabled: !configuration.isCalendarEnabled,
        });
      },
    },
    {
      icon: KanbanIcon,
      key: "isBoardEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.kanban.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.kanban.description",
      ),
      isEnabled: configuration.isBoardEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          isBoardEnabled: !configuration.isBoardEnabled,
        });
      },
    },
    {
      icon: TimeSheetIcon,
      key: "areTimeSheetsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.timeSheets.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.timeSheets.description",
      ),
      isEnabled: configuration.areTimeSheetsEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          areTimeSheetsEnabled: !configuration.areTimeSheetsEnabled,
        });
      },
    },
    {
      icon: DiscussionsIcon,
      key: "areDiscussionsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.discussions.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.discussions.description",
      ),
      isEnabled: configuration.areDiscussionsEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          areDiscussionsEnabled: !configuration.areDiscussionsEnabled,
        });
      },
    },
    {
      icon: DocumentsIcon,
      key: "areDocumentsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.documents.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.documents.description",
      ),
      isEnabled: configuration.areDocumentsEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          areDocumentsEnabled: !configuration.areDocumentsEnabled,
        });
      },
    },
    {
      icon: BudgetIcon,
      key: "isBudgetEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.budget.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.budget.description",
      ),
      isEnabled: configuration.isBudgetEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          isBudgetEnabled: !configuration.isBudgetEnabled,
        });
      },
    },
    {
      icon: ReportsIcon,
      key: "areReportsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.reports.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.reports.description",
      ),
      isEnabled: configuration.areReportsEnabled,
      enableParameter: () => {
        setConfiguration({
          ...configuration,
          areReportsEnabled: !configuration.areReportsEnabled,
        });
      },
    },
  ];

  const isEverythingChecked = () =>
    Object.values(configuration).every((config) => config);

  return (
    <div className={styles.projectToolsConfiguration}>
      <React.Fragment>
        <div className={styles.globalSettings}>
          <h3 className={styles.title}>
            {t("projectDetails.toolsConfiguration.title")}
          </h3>
          <div className={styles.input}>
            <p className={styles.label}>
              {t("projectDetails.toolsConfiguration.label")}
            </p>
            <div className={styles.toggle}>
              <ToggleInput
                isToggled={isEverythingChecked()}
                toggle={() =>
                  isEverythingChecked()
                    ? setConfiguration(
                        Object.keys(projectConfiguration).reduce(
                          (conf, key) => ({ ...conf, [key]: false }),
                          projectConfiguration,
                        ),
                      )
                    : setConfiguration(
                        Object.keys(projectConfiguration).reduce(
                          (conf, key) => ({ ...conf, [key]: true }),
                          projectConfiguration,
                        ),
                      )
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.parameters}>
          {parameters.map(({ icon: Icon, ...parameter }) => (
            <div
              key={parameter.key}
              className={classNames(styles.parameter, {
                [styles.timeSheets]: parameter.key === "areTimeSheetsEnabled",
              })}
            >
              <div className={styles.iconBox}>
                <Icon className={styles.icon} />
              </div>
              <p className={styles.name}>{parameter.name}</p>
              <p className={styles.description}>{parameter.description}</p>
              <div className={styles.toggle}>
                <ToggleInput
                  isToggled={parameter.isEnabled}
                  toggle={parameter.enableParameter}
                />
              </div>
            </div>
          ))}
        </div>
      </React.Fragment>
    </div>
  );
}

export { ProjectToolsConfiguration };
